import { buildChartTooltip } from './chartTooltip'

const DEFAULT_BACKGROUND_COLOR = '#FFFFFF'
const DEFAULT_TEXT_COLOR = '#262626'
const DEFAULT_ACCENT_COLOR = '#002F87'
const DEFAULT_INNER_RADIUS = 49
const DEFAULT_OUTER_RADIUS = 157.5
const DEFAULT_LABEL_EDGE_DISTANCE = 13
const DEFAULT_LABEL_RULE_WIDTH = 96
const DEFAULT_LABEL_VERTICAL_PADDING = 3
const DEFAULT_LABEL_HORIZONTAL_PADDING = 2
const DEFAULT_LABEL_MAX_LENGTH = 22
const DEFAULT_LEGEND_ITEM_GAP = 10
const DEFAULT_LEGEND_TEXT_HORIZONTAL_PADDING = 5
const DEFAULT_TITLE_TOP = 28
const DEFAULT_TITLE_HEIGHT = 16
const DEFAULT_TITLE_TO_CHART_GAP = 50
const DEFAULT_CHART_TO_HINT_GAP = 31
const DEFAULT_CENTER_TOTAL_FONT_SIZE = 33

const resolveLabelSide = (index, itemCount, startAngle, clockwise) => {
  const anglePerItem = 360 / itemCount
  const midAngle = clockwise ? startAngle - anglePerItem * (index + 0.5) : startAngle + anglePerItem * (index + 0.5)
  const midAngleInRadians = (midAngle * Math.PI) / 180

  return Math.cos(midAngleInRadians) >= 0 ? 'right' : 'left'
}

const formatCenterTotal = (total) => String(total)
const formatValue = (value) => String(value)
const formatPercent = (percent) => `${percent.toFixed(2)}%`

const truncateLabel = (label, maxLength) => {
  if (label.length <= maxLength) {
    return label
  }

  return `${label.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`
}

const resolveChartCenterY = (title, chartHeight, outerRadius) => {
  if (title && typeof chartHeight === 'number') {
    return DEFAULT_TITLE_TOP + DEFAULT_TITLE_HEIGHT + DEFAULT_TITLE_TO_CHART_GAP + outerRadius
  }

  if (title) {
    return '52%'
  }

  return '50%'
}

const resolveTotalTextTop = (chartCenterY, title) => {
  if (typeof chartCenterY === 'number') {
    return chartCenterY - DEFAULT_CENTER_TOTAL_FONT_SIZE / 2
  }

  if (title) {
    return '49.5%'
  }

  return '47.5%'
}

const resolveHintTop = (chartCenterY, outerRadius, hintOffsetY = 0) => {
  if (typeof chartCenterY !== 'number') {
    return undefined
  }

  return chartCenterY + outerRadius + DEFAULT_CHART_TO_HINT_GAP + hintOffsetY
}

const buildTitleGraphic = (title) => ({
  type: 'text',
  left: 'center',
  top: DEFAULT_TITLE_TOP,
  z: 10,
  style: {
    text: title,
    fill: DEFAULT_ACCENT_COLOR,
    fontFamily: 'Encode Sans',
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 16,
    letterSpacing: 0.15,
    align: 'center',
    verticalAlign: 'top',
  },
})

const buildHintGraphic = ({ text, side, labelEdgeDistance, hintTop }) => {
  const sidePosition = side === 'left' ? { left: labelEdgeDistance } : { right: labelEdgeDistance }

  return {
    type: 'text',
    ...sidePosition,
    ...(typeof hintTop === 'number' ? { top: hintTop } : { bottom: 24 }),
    z: 10,
    style: {
      text,
      fill: DEFAULT_ACCENT_COLOR,
      fontFamily: 'Encode Sans',
      fontSize: 10,
      fontWeight: 400,
      lineHeight: 16,
      letterSpacing: 0.4,
      align: side,
      verticalAlign: typeof hintTop === 'number' ? 'top' : 'bottom',
    },
  }
}

const buildLegendOption = (showLegend) => {
  if (!showLegend) {
    return undefined
  }

  return {
    top: 'bottom',
    itemGap: DEFAULT_LEGEND_ITEM_GAP,
    textStyle: {
      padding: [0, DEFAULT_LEGEND_TEXT_HORIZONTAL_PADDING, 0, DEFAULT_LEGEND_TEXT_HORIZONTAL_PADDING],
    },
  }
}

const buildToolboxOption = (showToolbox) => {
  if (!showToolbox) {
    return undefined
  }

  return {
    show: true,
    feature: {
      mark: { show: true },
      dataView: { show: true, readOnly: false },
      restore: { show: true },
      saveAsImage: { show: true },
    },
  }
}

const buildTooltipOption = (showTooltip) => {
  if (!showTooltip) {
    return { show: false }
  }

  return buildChartTooltip({
    getContent: ({ name, value, percent, color }) => ({
      title: name ?? '',
      rows: [
        {
          color,
          text: `${value ?? ''} (${formatPercent(percent ?? 0)})`,
        },
      ],
    }),
  })
}

export const buildDonutChartOption = ({
  data,
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  showLegend = false,
  showToolbox = false,
  startAngle = 90,
  clockwise = true,
  innerRadius = DEFAULT_INNER_RADIUS,
  outerRadius = DEFAULT_OUTER_RADIUS,
  labelEdgeDistance = DEFAULT_LABEL_EDGE_DISTANCE,
  title,
  leftHint,
  rightHint,
  showLeftHint = true,
  showRightHint = true,
  disableHoverAnimation = false,
  showTooltip = true,
  showRule = false,
  forceLeftLabelTextAlign = true,
  singleLineLabel = false,
  labelVerticalPadding = DEFAULT_LABEL_VERTICAL_PADDING,
  labelHorizontalPadding = DEFAULT_LABEL_HORIZONTAL_PADDING,
  labelMaxLength = DEFAULT_LABEL_MAX_LENGTH,
  chartHeight,
  hintOffsetY = 0,
}) => {
  const total = data.reduce((accumulator, item) => accumulator + item.value, 0)
  const visibleData = data.filter((item) => item.value > 0)
  const numericChartHeight = typeof chartHeight === 'number' ? chartHeight : undefined
  const chartCenterY = resolveChartCenterY(title, numericChartHeight, outerRadius)
  const totalTextTop = resolveTotalTextTop(chartCenterY, title)
  const hintTop = resolveHintTop(chartCenterY, outerRadius, hintOffsetY)
  const graphic = [
    {
      type: 'text',
      left: 'center',
      top: totalTextTop,
      y: 0,
      z: 10,
      style: {
        text: formatCenterTotal(total),
        fill: DEFAULT_TEXT_COLOR,
        fontFamily: 'Encode Sans',
        fontSize: 33,
        fontWeight: 600,
        align: 'center',
        verticalAlign: 'top',
      },
    },
  ]

  if (title) {
    graphic.push(buildTitleGraphic(title))
  }

  if (leftHint && showLeftHint) {
    graphic.push(buildHintGraphic({ text: leftHint, side: 'left', labelEdgeDistance, hintTop }))
  }

  if (rightHint && showRightHint) {
    graphic.push(buildHintGraphic({ text: rightHint, side: 'right', labelEdgeDistance, hintTop }))
  }

  return {
    backgroundColor,
    legend: buildLegendOption(showLegend),
    toolbox: buildToolboxOption(showToolbox),
    tooltip: buildTooltipOption(showTooltip),
    series: [
      {
        name: 'Donut chart',
        type: 'pie',
        radius: [innerRadius, outerRadius],
        center: ['50%', chartCenterY],
        startAngle,
        clockwise,
        roseType: 'radius',
        itemStyle: {
          borderRadius: 0,
        },
        emphasis: {
          scale: !disableHoverAnimation,
        },
        label: {
          show: true,
          position: 'outside',
          alignTo: 'edge',
          edgeDistance: labelEdgeDistance,
          distanceToLabelLine: -2,
        },
        labelLine: {
          show: true,
          length: 10,
          length2: 12,
          lineStyle: {
            width: 1,
          },
        },
        data: visibleData.map((item, index) => {
          const labelSide = item.labelSide ?? resolveLabelSide(index, visibleData.length, startAngle, clockwise)
          const textAlign = labelSide === 'right' ? 'right' : 'left'
          const labelTextAlign = forceLeftLabelTextAlign ? 'left' : textAlign
          const displayName = truncateLabel(item.name, labelMaxLength)

          return {
            value: item.value,
            name: item.name,
            selected: item.selected,
            itemStyle: {
              color: item.color,
            },
            label: {
              align: textAlign,
              formatter: ({ value, percent }) => {
                const metricText = `${formatValue(value)} (${formatPercent(percent)})`

                if (singleLineLabel) {
                  return `{name| ${displayName}: }{metric|${metricText}}`
                }

                if (showRule) {
                  return `{name|${displayName}:}\n{rule|}\n{metric|${metricText}}`
                }

                return `{name|${displayName}:}\n{metric|${metricText}}`
              },
              rich: {
                name: {
                  color: DEFAULT_TEXT_COLOR,
                  fontFamily: 'Encode Sans',
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: 18,
                  letterSpacing: 0,
                  align: labelTextAlign,
                  padding: singleLineLabel
                    ? [labelVerticalPadding, 0, labelVerticalPadding, labelHorizontalPadding]
                    : [labelVerticalPadding, labelHorizontalPadding, 4 + labelVerticalPadding, labelHorizontalPadding],
                },
                rule: {
                  width: DEFAULT_LABEL_RULE_WIDTH,
                  height: 1,
                  backgroundColor: item.color,
                  lineHeight: 0,
                  align: labelTextAlign,
                  padding: [0, labelHorizontalPadding, 4, labelHorizontalPadding],
                },
                metric: {
                  color: item.color,
                  fontFamily: 'Encode Sans',
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: 18,
                  letterSpacing: 0,
                  align: labelTextAlign,
                  padding: singleLineLabel
                    ? [labelVerticalPadding, 0, labelVerticalPadding, 0]
                    : [labelVerticalPadding, labelHorizontalPadding, labelVerticalPadding, labelHorizontalPadding],
                },
              },
            },
            labelLine: {
              lineStyle: {
                color: item.color,
                width: 1,
              },
            },
          }
        }),
      },
    ],
    graphic,
  }
}
